import { ReactComponent as InfoIcon } from '@assets/svg/info.svg';
import React, { MouseEventHandler } from 'react';
import { includes } from 'lodash';
import {
  ILineItemCurrency,
  ILineItemsData,
  ILineItemsDataValues,
  ILineItemsDetails,
  ISelectedItems,
} from '@common/interfaces/Common';
import { Tooltip } from 'antd';

const getTotals = (data: Array<ILineItemsData>, keys: Array<string>) => {
  const totals: any = {};

  keys.forEach((key) => {
    totals[key] = data.reduce(
      (previousValue, currentValue) => previousValue + (currentValue.values[key] as any),
      0,
    );
  });

  return totals;
};

export const SORT_VARIANTS = {
  LOWEST_OVERALL: 'Lowest overall',
};

export const parseDataToTableRows = (data: ILineItemsData[]) => {
  let result: ILineItemsDataValues[] = [];

  interface MappedLineItems {
    items: ILineItemsDataValues[];
    valueAggregator: ILineItemsDataValues;
  }

  data.reduce(
    (accumulator, currentValue, index) => {
      const { values, destination, order } = currentValue;
      if (!values) return accumulator;
      Object.keys(values).forEach((element) => {
        if (element !== 'line_item_destination') {
          const aggregatorValue = accumulator.valueAggregator[element] ?? 0;
          accumulator.valueAggregator[element] =
            (aggregatorValue as number) + ((values[element] as number) ?? 0);
        }
      });

      if (index === data.length - 1) {
        result = [
          ...accumulator.items,
          { ...values, line_item_destination: destination.name, id: index, order },
          {
            ...accumulator.valueAggregator,
            id: null,
          },
        ];
      }

      return {
        items: [
          ...accumulator.items,
          {
            ...values,
            line_item_destination: destination.name,
            id: index,
            order,
          },
        ],
        valueAggregator: accumulator.valueAggregator,
      };
    },
    { valueAggregator: {}, items: [] } as MappedLineItems,
  );

  return result;
};

export const mapRowDataToComponent = (
  record: number | null,
  lineItemData: ILineItemsDataValues,
  index: number,
  dataHasId: boolean,
  isSelected: boolean,
  onLineItemClick: MouseEventHandler,
  detail: ILineItemsDetails | null,
  setLineItemInfoSelected: any,
  element: string,
  currencies: ILineItemCurrency,
) => {
  if (!record) {
    return '-';
  }
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
    <div
      role="button"
      onClick={dataHasId ? onLineItemClick : () => null}
      className={`${dataHasId ? 'table-element-wrapper' : 'table-element-centered'} ${
        isSelected && 'selected'
      }`}
    >
      <span>{Number(record.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
      {dataHasId ? (
        <span className="table-element-currency">
          {`${currencies[element]} `}
          <InfoIcon
            onClick={(e) => {
              e.stopPropagation();
              setLineItemInfoSelected({ detail, index });
            }}
            className="tooltip-icon"
            data-testid="button-show-line-items"
          />
        </span>
      ) : (
        ` ${currencies[element]} `
      )}
    </div>
  );
};

const mapDataToColumns = (
  data: ILineItemsData[],
  currencies: ILineItemCurrency,
  selectedLineItems: ISelectedItems,
  onLineItemClick: (element: string, index: number) => void,
  setLineItemInfoSelected: any,
  sorting: string | null,
  sortedLine: number | null,
) => {
  if (!data.length || !data[0]?.values) return [];
  const keys = Object.keys(data[0]?.values);

  // last row check
  const getDataHasId = (id: number | null) => id !== null;
  const getIsSelected = (key: string, index: number) => {
    return includes(selectedLineItems[key], index);
  };

  const providerColumns = keys
    .filter((element) => element !== 'line_item_destination')
    .map((element) => ({
      title: element,
      key: element,
      dataIndex: element,
      render: (record: number | null, lineItemData: ILineItemsDataValues, index: number) => {
        if (!record) {
          return '-';
        }
        return mapRowDataToComponent(
          record,
          lineItemData,
          index,
          getDataHasId(lineItemData?.id as number),
          getIsSelected(element, index),
          () => onLineItemClick(element, lineItemData.order as number),
          data[index]?.details[element],
          setLineItemInfoSelected,
          element,
          currencies,
        );
      },
    }));

  if (sortedLine !== null) {
    providerColumns.sort((a, b) => {
      if (data[sortedLine].values[a.key] === null) {
        return 1;
      }
      if (data[sortedLine].values[b.key] === null) {
        return -1;
      }
      if ((data[sortedLine].values[a.key] as any) > (data[sortedLine].values[b.key] as any)) {
        return 1;
      }
      if ((data[sortedLine].values[a.key] as any) < (data[sortedLine].values[b.key] as any)) {
        return -1;
      }
      return 0;
    });
  }

  if (sorting === SORT_VARIANTS.LOWEST_OVERALL) {
    const totals = getTotals(data, keys);

    providerColumns.sort((a, b) => {
      if (totals[a.key] > totals[b.key]) {
        return 1;
      }
      if (totals[a.key] < totals[b.key]) {
        return -1;
      }
      return 0;
    });
  }

  const columns = [
    {
      title: 'DEST.',
      key: 'line_item_destination',
      dataIndex: 'line_item_destination',
      render: (record: string) => {
        return <Tooltip title={record}>{record}</Tooltip>;
      },
    },
    ...providerColumns,
  ];

  return columns;
};

export const getTableColumns = (
  assignmentData: ILineItemsData[],
  currencies: ILineItemCurrency,
  selectedLineItems: ISelectedItems,
  onLineItemClick: (element: string, index: number) => void,
  setLineItemInfoSelected: any,
  sorting: string | null,
  sortedLine: number | null,
) => [
  ...mapDataToColumns(
    assignmentData,
    currencies,
    selectedLineItems,
    onLineItemClick,
    setLineItemInfoSelected,
    sorting,
    sortedLine,
  ),
];

export const statusDropdownCustomStyles = {
  control: () => ({
    color: 'white',
    display: 'inline-flex',
    borderColor: 'white',
    borderRadius: '8px !important',
    width: '140px',
  }),
  singleValue: (styles: any) => ({
    ...styles,
    color: 'white',
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    svg: {
      fill: '#fff !important',
    },
  }),
};
